import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import i18n, {
  type AppLang,
  getCurrentLang,
  normalizeAppLang,
  readSavedLang,
  setAppLanguage,
} from "../i18n";

export type { AppLang };

type LanguageContextValue = {
  lang: AppLang;
  setLang: (l: AppLang) => Promise<void>;
  toggleLang: () => Promise<void>;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<AppLang>(() => readSavedLang());

  const setLang = useCallback(async (l: AppLang) => {
    await setAppLanguage(l);
    setLangState(l);
  }, []);

  const toggleLang = useCallback(async () => {
    setLangState((current) => {
      const next: AppLang = current === "en" ? "ta" : current === "ta" ? "ms" : "en";
      void setAppLanguage(next);
      return next;
    });
  }, []);

  useEffect(() => {
    const wanted = readSavedLang();
    if (getCurrentLang() !== wanted) {
      void setAppLanguage(wanted).then(() => setLangState(wanted));
    } else {
      setLangState(wanted);
    }
  }, []);

  useEffect(() => {
    const onLanguageChanged = (lng: string) => {
      setLangState(normalizeAppLang(lng));
    };
    i18n.on("languageChanged", onLanguageChanged);
    return () => {
      i18n.off("languageChanged", onLanguageChanged);
    };
  }, []);

  const value = useMemo(() => ({ lang, setLang, toggleLang }), [lang, setLang, toggleLang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside LanguageProvider");
  return ctx;
};
